/* eslint-disable max-lines */
import { createSlice } from "@reduxjs/toolkit";
import { currentDashboardExtraReducers } from "./extra-reducers";
import { setCurrentFilterValuesReducer } from "./reducers/setCurrentFilterValuesReducer";
import { setCurrentWidgetFilterValuesReducer } from "./reducers/setCurrentWidgetFilterValuesReducer";
import { setTopFilterReducer } from "./reducers/setTopFilterReducer";
import { updateCurrentStatusWidgetReducer } from "./reducers/updateCurrentStatusWidgetReducer";
import { DetailsPanelType, TimePeriods } from "./types";
import { setCurrentStatusSelection, setUnSelectedLegends } from "./utils";
import { getEnumValues } from "common/enum-helpers";
const promoteToWidgetLevel = (state, widgetStatus) => {
    if (!widgetStatus.isWidgetLevelTimePeriod && state.workspaceSettings?.dateSettings) {
        widgetStatus.dateSettingsDefaultValues = { ...widgetStatus.dateSettingsDefaultValues, ...state.workspaceSettings.dateSettings };
    }
    widgetStatus.isWidgetLevelTimePeriod = true;
};
const syncWorkspaceSettingsToWidgets = (state, committed, eligibleWidgetIds) => {
    const sections = state.currentStatus?.sections;
    if (!sections) {
        return;
    }
    const eligibleSet = new Set(eligibleWidgetIds);
    Object.values(sections).forEach((section) => {
        Object.entries(section?.widgets ?? {}).forEach(([widgetId, widget]) => {
            if (!widget || !eligibleSet.has(widgetId)) {
                return;
            }
            widget.isWidgetLevelTimePeriod = false;
            if (committed?.dateSettings) {
                widget.dateSettingsDefaultValues = { ...widget.dateSettingsDefaultValues, ...committed.dateSettings };
            }
            if (committed?.dataGranularity) {
                widget.dataGranularity = committed.dataGranularity;
            }
        });
    });
};
export const initialDashboardState = {
    currentBreakpoint: "lg",
    currentEntityId: "",
    currentFilterExpandedState: {},
    currentFilterValues: {},
    currentStatus: {},
    currentType: "Fund",
    currentWidgetFilterValues: {},
    detailPanels: {
        [DetailsPanelType.DashboardFilter]: { type: DetailsPanelType.DashboardFilter },
        [DetailsPanelType.WidgetFilter]: { type: DetailsPanelType.WidgetFilter },
        [DetailsPanelType.DashboardAssistant]: { type: DetailsPanelType.DashboardAssistant },
        [DetailsPanelType.CffChangeModal]: { type: DetailsPanelType.CffChangeModal }
    },
    isApplicationBackButtonClicked: false,
    isEditMode: false,
    isManualChangeDate: false,
    isMaximizedMode: false
};
const slice = createSlice({
    extraReducers: currentDashboardExtraReducers,
    initialState: initialDashboardState,
    name: "currentDashboardReducers",
    reducers: {
        clearCurrentFilterValues: (state) => {
            state.currentFilterValues = {};
            state.currentFilterExpandedState = {};
        },
        clearCurrentStatusTableFilterSelectedValue: (state, action) => {
            const { sectionId, widgetId } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets?.[widgetId];
            if (currentStatusWidgets) {
                currentStatusWidgets.tableFilterSelectedValue = undefined;
            }
        },
        clearCurrentWidgetFilterValues: (state, action) => {
            if (action.payload && state?.currentWidgetFilterValues && state?.currentWidgetFilterValues[action.payload]) {
                state.currentWidgetFilterValues[action.payload] = {};
            }
        },
        clearDraftWorkspaceSettings: (state) => {
            state.draftWorkspaceSettings = undefined;
        },
        commitDraftWorkspaceSettings: (state, action) => {
            state.workspaceSettings = state.draftWorkspaceSettings;
            state.draftWorkspaceSettings = undefined;
            syncWorkspaceSettingsToWidgets(state, state.workspaceSettings, action.payload);
        },
        maximizeWidget: (state, action) => {
            state.isMaximizedMode = true;
            state.maximizedWidget = { scrollPositionBeforeMaximizing: action.payload.scrollPositionBeforeMaximizing,
                sectionId: action.payload.sectionId,
                widgetId: action.payload.widgetId };
        },
        minimizeWidget: (state) => {
            state.isMaximizedMode = false;
        },
        resetWorkspaceSettings: {
            prepare: (widgetIds) => ({ payload: widgetIds }),
            reducer: (state) => {
                state.workspaceSettings = undefined;
                state.draftWorkspaceSettings = undefined;
            }
        },
        setApplicationBackButtonClicked: (state, action) => {
            state.isApplicationBackButtonClicked = action.payload;
        },
        setCffChangeModal: (state, action) => {
            state.cffChangeModal = action.payload;
        },
        setCffChangeModalSelectedModel: (state, action) => {
            if (state.cffChangeModal) {
                state.cffChangeModal.selectedModelId = action.payload.id;
                state.cffChangeModal.isPredefined = action.payload.isPredefined;
            }
        },
        setChangeBenchmarkModal: (state, action) => {
            state.changeBenchmarkModal = action.payload;
        },
        setChangeBenchmarkSelectedBenchmark: (state, action) => {
            if (state.changeBenchmarkModal) {
                state.changeBenchmarkModal.selectedBenchmarkId = action.payload;
            }
        },
        setCurrentChartSeriesTypes: (state, action) => {
            const { sectionId, widgetId, chartSeriesTypes } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].chartSeriesTypes = chartSeriesTypes;
            }
        },
        setCurrentFilterExpandedState: (state, action) => {
            if (!state.currentFilterExpandedState) {
                state.currentFilterExpandedState = {};
            }
            state.currentFilterExpandedState[action.payload.filter] = action.payload.expandedUids;
        },
        setCurrentFilterValues: setCurrentFilterValuesReducer,
        setCurrentGridSwitch: (state, action) => {
            const { sectionId, widgetId, enabled } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].gridSwitch = enabled;
            }
        },
        setCurrentStatusChartType: (state, action) => {
            const { sectionId, widgetId, chartType } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].chartType = chartType;
            }
        },
        setCurrentStatusCustomDateRangeEndDate: (state, action) => {
            const { sectionId, widgetId, endDate } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].customDateRangeEndDate = endDate;
            }
        },
        setCurrentStatusCustomDateRangeStartDate: (state, action) => {
            const { sectionId, widgetId, startDate } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].customDateRangeStartDate = startDate;
            }
        },
        setCurrentStatusDataGranularity: (state, action) => {
            const { sectionId, widgetId, granularity } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].dataGranularity = granularity;
            }
        },
        setCurrentStatusDataScale: (state, action) => {
            const { sectionId, widgetId, scale } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].dataScale = scale;
            }
        },
        setCurrentStatusDatePickerEndDate: (state, action) => {
            const { props, endDate } = action.payload;
            props.widgetIds.forEach((widgetId) => {
                const widgetStatus = state.currentStatus.sections?.[props.sectionId]?.widgets?.[widgetId];
                if (widgetStatus?.dateSettingsDefaultValues) {
                    promoteToWidgetLevel(state, widgetStatus);
                    widgetStatus.dateSettingsDefaultValues.datePickerEndDate = endDate;
                }
            });
        },
        setCurrentStatusDatePickerStartDate: (state, action) => {
            const { props, startDate } = action.payload;
            props.widgetIds.forEach((widgetId) => {
                const widgetStatus = state.currentStatus.sections?.[props.sectionId]?.widgets?.[widgetId];
                if (widgetStatus?.dateSettingsDefaultValues) {
                    promoteToWidgetLevel(state, widgetStatus);
                    widgetStatus.dateSettingsDefaultValues.datePickerStartDate = startDate;
                }
            });
        },
        setCurrentStatusDisplayOptions: (state, action) => {
            const { sectionId, widgetId, showTotals, showScale, showRowNumber, childrenId, showTotalRows } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].displayOptions = { showRowNumber, showScale, showTotalRows, showTotals };
                if (childrenId && currentStatusWidgets[childrenId]) {
                    currentStatusWidgets[childrenId].displayOptions = { showRowNumber, showScale, showTotalRows, showTotals };
                }
            }
        },
        setCurrentStatusDisplayValueType: (state, action) => {
            const { sectionId, widgetId, displayValueType, childrenId } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].displayValueType = displayValueType;
                if (childrenId && currentStatusWidgets[childrenId]) {
                    currentStatusWidgets[childrenId].displayValueType = displayValueType;
                }
            }
        },
        setCurrentStatusExcludeSoldAssets: (state, action) => {
            const { sectionId, widgetId, excludeSoldAssets } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].excludeSoldAssets = excludeSoldAssets;
            }
        },
        setCurrentStatusFromPeriod: (state, action) => {
            const { props, fromPeriod } = action.payload;
            props.widgetIds.forEach((widgetId) => {
                const widgetStatus = state.currentStatus.sections?.[props.sectionId]?.widgets?.[widgetId];
                if (widgetStatus?.dateSettingsDefaultValues) {
                    promoteToWidgetLevel(state, widgetStatus);
                    widgetStatus.dateSettingsDefaultValues.fromPeriod = fromPeriod;
                    widgetStatus.dateSettingsDefaultValues.selectedRadio = TimePeriods.SpecifiedPeriodToAsOfDate;
                }
            });
        },
        setCurrentStatusHasDatePicker: (state, action) => {
            const { sectionId, widgetId, value } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].hasDatePicker = value;
            }
        },
        setCurrentStatusLastPeriod: (state, action) => {
            const { props, lastPeriod } = action.payload;
            props.widgetIds.forEach((widgetId) => {
                const widgetStatus = state.currentStatus.sections?.[props.sectionId]?.widgets?.[widgetId];
                if (widgetStatus?.dateSettingsDefaultValues) {
                    promoteToWidgetLevel(state, widgetStatus);
                    widgetStatus.dateSettingsDefaultValues.lastPeriod = lastPeriod;
                }
            });
        },
        setCurrentStatusLastPeriodStep: (state, action) => {
            const { props, lastPeriodStep } = action.payload;
            props.widgetIds.forEach((widgetId) => {
                const widgetStatus = state.currentStatus.sections?.[props.sectionId]?.widgets?.[widgetId];
                if (widgetStatus?.dateSettingsDefaultValues) {
                    promoteToWidgetLevel(state, widgetStatus);
                    widgetStatus.dateSettingsDefaultValues.lastPeriodStep = lastPeriodStep;
                }
            });
        },
        setCurrentStatusMasterDetails: (state, action) => {
            const { sectionId, widgetId, selectedValue, childrenId } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (childrenId && currentStatusWidgets) {
                currentStatusWidgets[widgetId].masterDetailsChoice = selectedValue;
                currentStatusWidgets[childrenId].masterDetailsChoice = selectedValue;
            }
        },
        setCurrentStatusPeriodOfAnalysis: (state, action) => {
            const { sectionId, widgetId, periodOfAnalysis } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].periodOfAnalysis = periodOfAnalysis;
            }
        },
        setCurrentStatusReferenceDate: (state, action) => {
            const { sectionId, widgetId, referenceDate } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId] && referenceDate instanceof Date) {
                currentStatusWidgets[widgetId].referenceDate = referenceDate;
            }
        },
        setCurrentStatusSectionWidget: (state, action) => {
            const { sectionId, widgetId } = action.payload;
            state.currentStatus.sections = {
                ...state.currentStatus.sections,
                [sectionId]: {
                    widgets: {
                        [widgetId]: {}
                    }
                }
            };
        },
        setCurrentStatusSelected: (state, action) => {
            setCurrentStatusSelection(state, action.payload);
        },
        setCurrentStatusSelectedGroupedWidget: (state, action) => {
            const { sectionId, widgetId, groupId } = action.payload;
            const currentStatusSelectedGroupedWidget = state.currentStatus.sections?.[sectionId]?.selectedGroupedWidget;
            if (currentStatusSelectedGroupedWidget && groupId) {
                currentStatusSelectedGroupedWidget[groupId] = widgetId;
            }
        },
        setCurrentStatusSelectedRadioTimePeriod: (state, action) => {
            const { props, selectedRadio } = action.payload;
            props.widgetIds.forEach((widgetId) => {
                const widgetStatus = state.currentStatus.sections?.[props.sectionId]?.widgets?.[widgetId];
                if (widgetStatus?.dateSettingsDefaultValues) {
                    promoteToWidgetLevel(state, widgetStatus);
                    widgetStatus.dateSettingsDefaultValues.selectedRadio = selectedRadio;
                }
            });
        },
        setCurrentStatusShownValues: (state, action) => {
            const { sectionId, widgetId, shownValues, childrenId } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].shownValues = shownValues;
                currentStatusWidgets[widgetId].masterDetailsChoice = "";
                if (childrenId) {
                    currentStatusWidgets[childrenId].masterDetailsChoice = "";
                }
            }
        },
        setCurrentStatusSortBy: (state, action) => {
            const { sectionId, widgetId, sortBy } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].sortBy = sortBy;
            }
        },
        setCurrentStatusTableFilterSelectedValue: (state, action) => {
            const { sectionId, widgetId, value } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].tableFilterSelectedValue = value;
            }
        },
        setCurrentStatusTenor: (state, action) => {
            const { sectionId, widgetId, selectedTenor } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].defaultTenor = selectedTenor;
            }
        },
        setCurrentStatusUnselectedLegends: (state, action) => {
            const { childrenId } = action.payload;
            setUnSelectedLegends(state, action.payload);
            if (childrenId) {
                setUnSelectedLegends(state, { ...action.payload, widgetId: childrenId });
            }
        },
        setCurrentStatusViewForm: (state, action) => {
            const { sectionId, widgetId, viewForm } = action.payload;
            const currentStatusWidgets = state.currentStatus.sections?.[sectionId]?.widgets;
            if (currentStatusWidgets && currentStatusWidgets[widgetId]) {
                currentStatusWidgets[widgetId].viewForm = viewForm;
            }
        },
        setCurrentWidgetFilterValues: setCurrentWidgetFilterValuesReducer,
        setDashboardCurrency: (state, action) => {
            state.currency = action.payload;
        },
        setDashboardManualChangeDate: (state, action) => {
            state.isManualChangeDate = action.payload;
        },
        setDashboardOptions: (state, action) => {
            state.dashboardOptions = { ...state.dashboardOptions, ...action.payload };
        },
        setDashboardReferenceDate: (state, action) => {
            state.referenceDate = action.payload;
        },
        setDetailsPanel: (state, action) => {
            if ((action.payload.type || action.payload.type === 0) && state.detailPanels) {
                const panelType = action.payload.type;
                if (action.payload.isOpen) {
                    const panelTypes = getEnumValues(DetailsPanelType);
                    let maxZIndex = 111;
                    panelTypes.forEach((type) => {
                        if (state.detailPanels && type !== panelType) {
                            const panelState = state.detailPanels[type];
                            if (state.detailPanels[DetailsPanelType.DashboardAssistant]?.isOpen) {
                                if (panelState?.zIndex) {
                                    maxZIndex = Math.max(maxZIndex, panelState.zIndex);
                                }
                            }
                            else if (panelState) {
                                panelState.isOpen = false;
                            }
                        }
                    });
                    action.payload = { ...action.payload, zIndex: maxZIndex + 1 };
                }
                state.detailPanels[panelType] = action.payload;
            }
        },
        setIncludeHistoricalCashflows: (state, action) => {
            const { sectionId, enabled } = action.payload;
            const section = state.currentStatus.sections?.[sectionId];
            if (section) {
                section.includeHistoricalCashflows = enabled;
            }
        },
        setIncludePacingPlan: (state, action) => {
            const { enabled, sectionId } = action.payload;
            const section = state.currentStatus.sections?.[sectionId];
            if (section) {
                section.includePacingPlan = enabled;
                section.includeHistoricalCashflows = !enabled;
            }
        },
        setSectionCustomDateRangeEndDate: (state, action) => {
            const { sectionId, endDate } = action.payload;
            if (!state.currentStatus.sections) {
                state.currentStatus.sections = {};
            }
            if (!state.currentStatus.sections[sectionId]) {
                state.currentStatus.sections[sectionId] = { widgets: {} };
            }
            state.currentStatus.sections[sectionId].sectionCustomDateRangeEndDate = endDate;
        },
        setSectionCustomDateRangeStartDate: (state, action) => {
            const { sectionId, startDate } = action.payload;
            if (!state.currentStatus.sections) {
                state.currentStatus.sections = {};
            }
            if (!state.currentStatus.sections[sectionId]) {
                state.currentStatus.sections[sectionId] = { widgets: {} };
            }
            state.currentStatus.sections[sectionId].sectionCustomDateRangeStartDate = startDate;
        },
        setSectionDateSettingsDatePickerEndDate: (state, action) => {
            const { sectionId, endDate } = action.payload;
            const section = state.currentStatus.sections?.[sectionId];
            if (section) {
                if (!section.dateSettingsSectionValues) {
                    section.dateSettingsSectionValues = {};
                }
                section.dateSettingsSectionValues.datePickerEndDate = endDate;
            }
        },
        setSectionDateSettingsDatePickerStartDate: (state, action) => {
            const { sectionId, startDate } = action.payload;
            const section = state.currentStatus.sections?.[sectionId];
            if (section) {
                if (!section.dateSettingsSectionValues) {
                    section.dateSettingsSectionValues = {};
                }
                section.dateSettingsSectionValues.datePickerStartDate = startDate;
            }
        },
        setSectionDateSettingsFromPeriod: (state, action) => {
            const { sectionId, fromPeriod } = action.payload;
            const section = state.currentStatus.sections?.[sectionId];
            if (section) {
                if (!section.dateSettingsSectionValues) {
                    section.dateSettingsSectionValues = {};
                }
                section.dateSettingsSectionValues.fromPeriod = fromPeriod;
                section.dateSettingsSectionValues.selectedRadio = TimePeriods.SpecifiedPeriodToAsOfDate;
            }
        },
        setSectionDateSettingsLastPeriod: (state, action) => {
            const { sectionId, lastPeriod } = action.payload;
            const section = state.currentStatus.sections?.[sectionId];
            if (section) {
                if (!section.dateSettingsSectionValues) {
                    section.dateSettingsSectionValues = {};
                }
                section.dateSettingsSectionValues.lastPeriod = lastPeriod;
            }
        },
        setSectionDateSettingsLastPeriodStep: (state, action) => {
            const { sectionId, lastPeriodStep } = action.payload;
            const section = state.currentStatus.sections?.[sectionId];
            if (section) {
                if (!section.dateSettingsSectionValues) {
                    section.dateSettingsSectionValues = {};
                }
                section.dateSettingsSectionValues.lastPeriodStep = lastPeriodStep;
            }
        },
        setSectionDateSettingsSelectedRadio: (state, action) => {
            const { sectionId, selectedRadio } = action.payload;
            const section = state.currentStatus.sections?.[sectionId];
            if (section) {
                if (!section.dateSettingsSectionValues) {
                    section.dateSettingsSectionValues = {};
                }
                section.dateSettingsSectionValues.selectedRadio = selectedRadio;
            }
        },
        setSectionTenor: (state, action) => {
            const { sectionId, tenor } = action.payload;
            if (!state.currentStatus.sections) {
                state.currentStatus.sections = {};
            }
            if (!state.currentStatus.sections[sectionId]) {
                state.currentStatus.sections[sectionId] = { widgets: {} };
            }
            state.currentStatus.sections[sectionId].sectionTenor = tenor;
        },
        setSmartFilterLoading: (state, action) => {
            state.smartFilterLoading = action.payload;
        },
        setSmartFilterShowSelectedOnly: (state, action) => {
            state.smartFilterShowSelectedOnly = action.payload;
        },
        setTopFilter: setTopFilterReducer,
        setWidgetIsWidgetLevelTimePeriod: (state, action) => {
            const { sectionId, widgetId, isWidgetLevel } = action.payload;
            const currentWidget = state.currentStatus?.sections?.[sectionId]?.widgets?.[widgetId];
            if (currentWidget) {
                currentWidget.isWidgetLevelTimePeriod = isWidgetLevel;
            }
        },
        setWidgetLoadStatusToTrueOrFalse: (state, action) => {
            const { sectionId, widgetId, isLoaded } = action.payload;
            const currentWidget = state.currentStatus?.sections?.[sectionId]?.widgets?.[widgetId];
            if (currentWidget) {
                currentWidget.isLoaded = isLoaded;
            }
        },
        setWorkspaceSettings: (state, action) => {
            state.workspaceSettings = action.payload;
        },
        updateCurrentStatusWidget: updateCurrentStatusWidgetReducer,
        updateDraftWorkspaceDateSettings: (state, action) => {
            state.draftWorkspaceSettings = {
                ...state.draftWorkspaceSettings,
                dateSettings: { ...state.draftWorkspaceSettings?.dateSettings, ...action.payload }
            };
        },
        updateDraftWorkspaceSettings: (state, action) => {
            state.draftWorkspaceSettings = { ...state.draftWorkspaceSettings, ...action.payload };
        }
    }
});
export const currentDashboardReducers = slice.reducer;
export const { clearCurrentFilterValues, clearCurrentWidgetFilterValues, clearCurrentStatusTableFilterSelectedValue, maximizeWidget, minimizeWidget, setCffChangeModal, setCffChangeModalSelectedModel, setChangeBenchmarkModal, setChangeBenchmarkSelectedBenchmark, setCurrentFilterExpandedState, setCurrentFilterValues, setCurrentWidgetFilterValues, setCurrentStatusChartType, setCurrentChartSeriesTypes, setCurrentStatusSectionWidget, setCurrentStatusDataGranularity, setCurrentStatusDisplayOptions, setCurrentStatusDisplayValueType, setCurrentStatusExcludeSoldAssets, setCurrentStatusMasterDetails, setCurrentStatusShownValues, setCurrentStatusSelected, setCurrentStatusSelectedGroupedWidget, setCurrentStatusSortBy, setCurrentStatusTableFilterSelectedValue, setCurrentStatusViewForm, setCurrentStatusUnselectedLegends, setDashboardCurrency, setDashboardOptions, setDashboardReferenceDate, setDetailsPanel, setCurrentStatusDataScale, setCurrentStatusReferenceDate, setCurrentStatusHasDatePicker, setCurrentGridSwitch, setCurrentStatusPeriodOfAnalysis, setTopFilter, setWidgetLoadStatusToTrueOrFalse, setApplicationBackButtonClicked, setDashboardManualChangeDate, updateCurrentStatusWidget, setCurrentStatusSelectedRadioTimePeriod, setCurrentStatusCustomDateRangeEndDate, setCurrentStatusCustomDateRangeStartDate, setCurrentStatusDatePickerEndDate, setCurrentStatusDatePickerStartDate, setCurrentStatusFromPeriod, setCurrentStatusLastPeriod, setCurrentStatusLastPeriodStep, setCurrentStatusTenor, setIncludeHistoricalCashflows, setSectionCustomDateRangeEndDate, setSectionCustomDateRangeStartDate, setSectionDateSettingsDatePickerEndDate, setSectionDateSettingsDatePickerStartDate, setSectionDateSettingsFromPeriod, setSectionDateSettingsLastPeriod, setSectionDateSettingsLastPeriodStep, setIncludePacingPlan, setSectionDateSettingsSelectedRadio, setSectionTenor, setSmartFilterLoading, setSmartFilterShowSelectedOnly, setWorkspaceSettings, updateDraftWorkspaceSettings, updateDraftWorkspaceDateSettings, commitDraftWorkspaceSettings, clearDraftWorkspaceSettings, resetWorkspaceSettings, setWidgetIsWidgetLevelTimePeriod } = slice.actions;
export const isTenorPreviewUpdatedAction = (action) => Boolean([setCurrentStatusTenor].find((e) => e.type === action.type));
